import React, { useState, useRef, useEffect } from 'react';
import { 
  BookOpen, 
  Globe, 
  Rocket, 
  Shield,
  Sparkles,
  Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './VissionMission.css';

const AnimatedVisionMissionPage = () => {
  const [activeStrategy, setActiveStrategy] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef(null);

  const strategicPillars = [
    {
      icon: BookOpen,
      title: "Transformative Learning",
      description: "Revolutionizing education through innovative, personalized learning experiences that adapt to individual potential.",
      color: "#5A67D8",
      gradient: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)"
    },
    {
      icon: Globe,
      title: "Global Connectivity",
      description: "Bridging cultural barriers and fostering global understanding through immersive, interactive educational platforms.",
      color: "#38B2AC",
      gradient: "linear-gradient(135deg, #4FD1C5 0%, #38B2AC 100%)"
    },
    {
      icon: Rocket,
      title: "Innovative Acceleration",
      description: "Empowering breakthrough thinking and creative problem-solving through cutting-edge learning methodologies.",
      color: "#ED64A6",
      gradient: "linear-gradient(135deg, #ED64A6 0%, #9F7AEA 100%)"
    },
    {
      icon: Shield,
      title: "Ethical Leadership",
      description: "Cultivating responsible, aware individuals committed to making meaningful societal contributions.",
      color: "#4299E1",
      gradient: "linear-gradient(135deg, #4299E1 0%, #3182CE 100%)"
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight;
        const winHeight = window.innerHeight;
        const scrollPercent = scrollTop / (docHeight - winHeight);
        setScrollProgress(scrollPercent);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div 
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="animated-vision-container"
    >
      {/* Scroll Progress Indicator */}
      <motion.div 
        className="scroll-progress-bar"
        style={{ 
          scaleX: scrollProgress,
          transformOrigin: 'left center'
        }}
      />

      <div className="animated-content-wrapper">
        {/* Vision Section with Sparkle Effect */}
        <motion.section 
          className="animated-vision-section"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="vision-sparkle-background">
            <Sparkles 
              size={24} 
              strokeWidth={1.5} 
              className="sparkle-icon top-left" 
            />
            <Sparkles 
              size={24} 
              strokeWidth={1.5} 
              className="sparkle-icon bottom-right" 
            />
          </div>
          <div className="animated-vision-content">
            <h2 className="animated-section-title">
              Our Vision
            </h2>
            <p className="animated-vision-description">
              Pioneering an transformative educational ecosystem that transcends traditional boundaries, 
              empowering individuals to unlock their extraordinary potential through personalized, 
              innovative, and holistic learning experiences.
            </p>
          </div>
        </motion.section>

        {/* Strategic Pillars Section */}
        <section className="animated-strategy-section">
          <h2 className="animated-section-title">
            our Mission
            <Layers size={32} className="title-icon" />
          </h2>

          <div className="animated-strategy-grid">
            <AnimatePresence>
              {strategicPillars.map((strategy, index) => {
                const StrategyIcon = strategy.icon;
                return (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ 
                      opacity: 1, 
                      scale: activeStrategy === index ? 1.05 : 1 
                    }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4 }}
                    className={`animated-strategy-card ${activeStrategy === index ? 'strategy-active' : ''}`}
                    style={{ 
                      '--strategy-gradient': strategy.gradient,
                      '--strategy-color': strategy.color
                    }}
                    onMouseEnter={() => setActiveStrategy(index)}
                    onMouseLeave={() => setActiveStrategy(null)}
                  >
                    <div className="strategy-card-inner">
                      <motion.div 
                        className="strategy-icon"
                        whileHover={{ 
                          rotate: [0, -10, 10, 0],
                          scale: 1.1 
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <StrategyIcon strokeWidth={1.5} size={48} />
                      </motion.div>
                      <h3 className="strategy-title">{strategy.title}</h3>
                      <p className="strategy-description">{strategy.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default AnimatedVisionMissionPage;